import type { ComponentType } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { icons } from 'lucide-react';

export type LucideIcon = {
  friendlyName: string;
  name: string;
  type: 'lucide' | 'custom';
  Component?: ComponentType;
  url?: string; // For custom icons
};

const ICONS_PER_PAGE = 48; // 6 columns x 8 rows

// Pre-computed sorted list of Lucide icons (the `icons` export is already deduplicated)
const LUCIDE_ICONS: LucideIcon[] = Object.entries(icons)
  .map(([name, Component]) => ({
    Component,
    friendlyName: name.replace(/([A-Z])/g, ' $1').trim(),
    name,
    type: 'lucide' as const,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export function useLucideIconPicker(customIcons?: LucideIcon[]) {
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState(0);

  // Get all available icons (Lucide + custom)
  const allIcons: LucideIcon[] = useMemo(
    () => [...(customIcons || []), ...LUCIDE_ICONS],
    [customIcons]
  );

  // Search/filter icons
  const filteredIcons = useMemo(() => {
    if (!search.trim()) return allIcons;

    const lowerSearch = search.toLowerCase();
    return allIcons.filter(
      icon =>
        icon.name.toLowerCase().includes(lowerSearch) ||
        icon.friendlyName.toLowerCase().includes(lowerSearch)
    );
  }, [allIcons, search]);

  // Paginate filtered icons
  const paginatedIcons = useMemo(() => {
    return filteredIcons.slice(0, cursor + ICONS_PER_PAGE);
  }, [filteredIcons, cursor]);

  const hasMore = cursor + ICONS_PER_PAGE < filteredIcons.length;

  const loadMore = useCallback(() => {
    if (!hasMore) {
      return;
    }

    setCursor(prev => prev + ICONS_PER_PAGE);
  }, [hasMore]);

  const reset = useCallback(() => {
    setCursor(0);
  }, []);

  // Reset cursor when search changes
  const setSearchWithReset = useCallback((value: string) => {
    setSearch(value);
    setCursor(0);
  }, []);

  return {
    hasMore,
    icons: paginatedIcons,
    loadMore,
    reset,
    search,
    setSearch: setSearchWithReset,
  };
}
